import { createSlice } from '@reduxjs/toolkit';
import axiosInstance from 'src/utils/axiosInstance';

const initialState = {
  users: [],
  totalCount: 0,
  totalPages: 0,
  loading: false,
  error: null,
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUsers: (state, action) => {
      state.users = action.payload;
    },
    setTotalCount: (state, action) => {
      state.totalCount = action.payload;
    },
    setTotalPages: (state, action) => {
      state.totalPages = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
  },
});

export const { setUsers, setTotalCount, setTotalPages, setLoading, setError } = userSlice.actions;

export const getUsers = (filters = {}) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const page = filters.page || 1;
    const limit = filters.limit || 10;
    const search = filters.search || '';
    const role_id = filters.role_id || '';
    const department_id = filters.department_id || filters.departmentId || filters.department || '';
    const designation_id = filters.designation_id || filters.designationId || filters.designation || '';
    const stateId = filters.stateId || '';
    const type = filters.type || '';
    const geofence_enabled = filters.geofence_enabled;
    // const multi_punch_enabled = filters.multi_punch_enabled;
    const params = { page, limit };
    if (search) params.search = search;
    if (role_id) params.role_id = role_id;
    if (department_id) params.departmentId = department_id;
    if (designation_id) params.designationId = designation_id;
    if (stateId) params.stateId = stateId;
    if (type) params.type = type;
    if (geofence_enabled !== undefined && geofence_enabled !== '') params.geofence_enabled = geofence_enabled;
    // if (multi_punch_enabled !== undefined && multi_punch_enabled !== '') params.multi_punch_enabled = multi_punch_enabled;
    const response = await axiosInstance.get('/admin/user/list', { params });
    if (response.data && response.data.status === true) {
      const result = response.data.data.result || [];
      const totalCount = response.data.data.totalCount || result.length;
      const totalPages = response.data.data.totalPages || 1;
      dispatch(setUsers(result));
      dispatch(setTotalCount(totalCount));
      dispatch(setTotalPages(totalPages));
      return { status: true, data: result, totalCount, totalPages };
    } else {
      dispatch(setUsers([]));
      dispatch(setTotalCount(0));
      dispatch(setTotalPages(0));
      const errorMsg = response.data?.message || 'Failed to fetch users';
      dispatch(setError(errorMsg));
      return { status: false, message: errorMsg };
    }
  } catch (error) {
    dispatch(setUsers([]));
    dispatch(setTotalCount(0));
    dispatch(setTotalPages(0));
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch users';
    dispatch(setError(errorMsg));
    return { status: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

export const getUserDetails = (id) => async () => {
  try {
    const response = await axiosInstance.get(`/admin/user/getbyId/${id}`);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data };
    } else {
      return { status: false, message: response.data?.message || 'Failed to fetch user details' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch user details';
    return { status: false, message: errorMsg };
  }
};

export const addUser = (payload) => async () => {
  try {
    const finalPayload = {
      ...payload,
      // multi_punch_enabled:
      //   payload.multi_punch_enabled !== undefined ? Boolean(payload.multi_punch_enabled) : false,
    };
    const response = await axiosInstance.post('/admin/user/add', finalPayload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to add user' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to add user';
    return { status: false, message: errorMsg };
  }
};

export const updateUser = (id, payload) => async () => {
  try {
    const finalPayload = {
      ...payload,
      // multi_punch_enabled:
      //   payload.multi_punch_enabled !== undefined ? Boolean(payload.multi_punch_enabled) : false,
    };
    const response = await axiosInstance.put(`/admin/user/update/${id}`, finalPayload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to update user' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to update user';
    return { status: false, message: errorMsg };
  }
};

// Upload user image (POST /api/v1/admin/user/upload-image/:id)
export const uploadUserImage = (id, fileOrFormData) => async () => {
  try {
    let formData;
    if (fileOrFormData instanceof FormData) {
      formData = fileOrFormData;
    } else {
      formData = new FormData();
      formData.append('image', fileOrFormData);
    }
    const response = await axiosInstance.post(`/admin/user/upload-image/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message || 'Image uploaded successfully' };
    } else {
      return { status: false, message: response.data?.message || 'Failed to upload user image' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to upload user image';
    return { status: false, message: errorMsg };
  }
};

export const toggleUserStatus = (id, payload) => async () => {
  try {
    const response = await axiosInstance.patch(`/admin/user/status/${id}`, payload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to toggle user status' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to toggle user status';
    return { status: false, message: errorMsg };
  }
};

export const deleteUser = (id) => async () => {
  try {
    const response = await axiosInstance.delete(`/admin/user/delete/${id}`);
    if (response.data && response.data.status === true) {
      return { status: true, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to delete user' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to delete user';
    return { status: false, message: errorMsg };
  }
};

// Export users data
export const exportUsers = (filters = {}) => async () => {
  try {
    const params = {};
    if (filters.type !== undefined) {
      params.type = filters.type;
    } else {
      params.type = 'employee';
    }
    if (filters.search) params.search = filters.search;
    if (filters.role_id) params.role_id = filters.role_id;
    if (filters.stateId) params.stateId = filters.stateId;
    if (filters.designationId) params.designationId = filters.designationId;
    if (filters.departmentId || filters.department_id) params.departmentId = filters.departmentId || filters.department_id;
    if (filters.status !== undefined && filters.status !== '') params.status = filters.status;
    if (filters.geofence_enabled !== undefined && filters.geofence_enabled !== '') params.geofence_enabled = filters.geofence_enabled;
    // if (filters.multi_punch_enabled !== undefined && filters.multi_punch_enabled !== '') params.multi_punch_enabled = filters.multi_punch_enabled;

    const response = await axiosInstance.get('/admin/user/export', {
      params,
      responseType: 'blob',
    });

    if (response.data && response.data.type === 'application/json') {
      const text = await response.data.text();
      const json = JSON.parse(text);
      if (json.status === false) {
        return { status: false, message: json.message || 'Failed to export users' };
      }
    }

    const blob = new Blob([response.data], {
      type: response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    const contentDisposition = response.headers['content-disposition'];
    let fileName = params.type === 'employee' ? 'employees_export.xlsx' : 'users_export.xlsx';
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match && match[1]) fileName = match[1];
    }
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return { status: true };
  } catch (error) {
    let errorMsg = 'Failed to export users';
    if (error.response?.data instanceof Blob && error.response.data.type === 'application/json') {
      try {
        const text = await error.response.data.text();
        const json = JSON.parse(text);
        errorMsg = json.message || errorMsg;
      } catch (e) {
        errorMsg = error.message || errorMsg;
      }
    } else {
      errorMsg = error.response?.data?.message || error.message || errorMsg;
    }
    return { status: false, message: errorMsg };
  }
};

export default userSlice.reducer;